import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "redux/store";
import { useFormik } from "formik";
import { bookingQueries } from "../../../redux/reducers/UserSlice/UserSlice";
import { useParams } from "react-router-dom";
import { booking } from "../../../redux/reducers/UserSlice/UserSliceType";
import successHandler from "utils/helpers/SuccessHandler";
import NomadDatePicker from "shared/DateField/NomadDatePicker";
import { eachDayOfInterval } from "date-fns";
import CommonModal from "shared/Modal/CommonModal";
import moment from "moment";
import * as Yup from "yup";
import { Modal } from "@mantine/core";
import { Calendar, Users, MessageSquare, Send, X, Home } from "lucide-react";

interface ContactOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: booking) => void;
  propertyData?: any;
  currentRoomId?: any;
  selectedRoom?: any; // Add selectedRoom prop
  roomHasAvailability?: boolean;
}

interface FormValues {
  check_in_date: string;
  check_out_date: string;
  guest_count: number;
  query_for: string;
  query: string;
}

const ContactOwnerModal: React.FC<ContactOwnerModalProps> = ({
  isOpen,
  onClose,
  onSend,
  propertyData = {},
  currentRoomId,
  selectedRoom, // Receive selectedRoom
  roomHasAvailability,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { slug } = useParams();
  const id = slug;
  const propertyId = id;
  const isSharedProperty = propertyData.is_shared_property;
  const [Description, setDescription] = useState("");
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Use selectedRoom or find by currentRoomId
  const currentRoom =
    selectedRoom ||
    (isSharedProperty && currentRoomId
      ? propertyData?.rooms?.find((item: any) => item.id === currentRoomId)
      : null);

  const calculateMonthsDifference = (
    bookingStart: string,
    bookingEnd: string
  ): number => {
    const start = moment(bookingStart, "YYYY-MM-DD");
    const end = moment(bookingEnd, "YYYY-MM-DD");
    
    // Calculate decimal months
    const months = end.diff(start, 'months', true);
    
    // Round up - any partial month counts as full month
    return Math.ceil(months);
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      check_in_date: "",
      check_out_date: "",
      guest_count: 1,
      query_for: currentRoom?.id || "",
      query: "",
    },
    validationSchema: Yup.object().shape({
      check_in_date: Yup.date()
        .nullable()
        .required("Check-in date is required"),
      check_out_date: Yup.date()
        .nullable()
        .required("Check-out date is required"),
      query: Yup.string().required("Message is required"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!currentUser?.profile_complete) {
        setIsContactModalOpen(true);
        setDescription(
          "Please complete your profile before making a booking query."
        );
        return;
      }

      const queryFor =
        isSharedProperty && values.query_for
          ? { room_number: [values.query_for] }
          : null;

      onSend(values);

      try {
        const result = await dispatch(
          bookingQueries({
            ...values,
            check_in_date: moment(values.check_in_date).format("YYYY-MM-DD"),
            check_out_date: moment(values.check_out_date).format("YYYY-MM-DD"),
            guest_count: values.guest_count || 1,
            query: values.query,
            query_for: queryFor,
            property_id: propertyId,
            no_of_months: calculateMonthsDifference(
              moment(values.check_in_date).format("YYYY-MM-DD"),
              moment(values.check_out_date).format("YYYY-MM-DD")
            ),
          })
        );

        if (bookingQueries.fulfilled.match(result)) {
          successHandler(
            "Your message was sent! You will be notified by email when a reply is received."
          );
          onClose();
          formik.resetForm();
        }
      } catch (error) {
        console.error("Failed to dispatch booking query:", error);
      }
    },
  });

  // Destructure formik values for cleaner usage
  const { check_in_date, check_out_date } = formik.values;

  // Set query_for when currentRoom changes
  useEffect(() => {
    if (currentRoom && formik.values.query_for !== currentRoom.id) {
      formik.setFieldValue("query_for", currentRoom.id);
    }
  }, [currentRoom?.id]);

  const getDisabledDates = (booked_dates: [string, string][]) => {
    if (!booked_dates || !Array.isArray(booked_dates)) {
      return [];
    }

    const disabledDates: Date[] = [];
    booked_dates.forEach(([startDate, endDate]) => {
      if (startDate && endDate) {
        const dateRange = eachDayOfInterval({
          start: moment(startDate).toDate(),
          end: moment(endDate).toDate(),
        });
        disabledDates.push(...dateRange);
      }
    });
    return disabledDates;
  };

  // Helper function to get minimum date
const getMinimumDate = () => {
  if (isSharedProperty && currentRoom) {
    // ✅ First check if next_available_date exists and is in the future
    if (currentRoom.next_available_date && typeof currentRoom.next_available_date === "string") {
      const availDate = moment(currentRoom.next_available_date);
      if (availDate.isAfter(moment())) {
        return availDate.toDate();  // ✅ Use next_available_date
      }
    }
    
    // Fall back to availability_start
    return moment(currentRoom?.availability_start).isAfter(moment())
      ? moment(currentRoom?.availability_start).toDate()
      : new Date();
  }
  
  // For non-shared properties
  if (propertyData?.next_available_date && typeof propertyData.next_available_date === "string") {
    const availDate = moment(propertyData.next_available_date);
    if (availDate.isAfter(moment())) {
      return availDate.toDate();
    }
  }
  
  return moment(propertyData?.availability_start).isAfter(moment())
    ? moment(propertyData?.availability_start).toDate()
    : new Date();
};

  // Helper function to get maximum date
const getMaximumDate = () => {
  if (isSharedProperty && currentRoom) {
    return currentRoom?.availability_end  // ✅ Use availability_end, not booking_end
      ? moment(currentRoom?.availability_end).toDate()
      : undefined;
  }
  return propertyData?.availability_end
    ? moment(propertyData?.availability_end).toDate()
    : undefined;
};

  // Disable specific dates
  const disableDate = (date: Date) => {
    const disabledDates = getDisabledDates(
      isSharedProperty && currentRoom
        ? currentRoom?.booked_dates
        : propertyData?.booked_dates
    );
    return disabledDates.some(
      (disabledDate) =>
        date.getFullYear() === disabledDate.getFullYear() &&
        date.getMonth() === disabledDate.getMonth() &&
        date.getDate() === disabledDate.getDate()
    );
  };

  // Check if selected room has availability
  const selectedRoomHasAvailability = roomHasAvailability;

  // Format today's date for comparison
  const formattedDate = moment().format("YYYY-MM-DD");

  // Debug logging
  console.log("=== DATE PICKER DEBUG ===");
  console.log("check_in_date:", check_in_date);
  console.log("check_out_date:", check_out_date);
  console.log("isSharedProperty:", isSharedProperty);
  console.log("currentRoom:", currentRoom);
  console.log("selectedRoomHasAvailability:", selectedRoomHasAvailability);
  console.log(
    "propertyData?.next_available_date:",
    propertyData?.next_available_date
  );
  console.log("formattedDate:", formattedDate);
  console.log("getMinimumDate():", getMinimumDate());
  console.log("getMaximumDate():", getMaximumDate());
  console.log(
    "Check-in disabled:",
    (isSharedProperty && !currentRoom) ||
      (isSharedProperty && currentRoom && !selectedRoomHasAvailability) ||
      (typeof propertyData?.next_available_date === "string" &&
        propertyData.next_available_date !== formattedDate)
  );
  console.log(
    "Check-out disabled:",
    (isSharedProperty && !currentRoom) ||
      (isSharedProperty && currentRoom && !selectedRoomHasAvailability) ||
      !check_in_date ||
      (typeof propertyData?.next_available_date === "string" &&
        propertyData.next_available_date !== formattedDate)
  );
  console.log("========================");

  if (!isOpen) return null;

  return (
    <React.Fragment>
      <Modal
        opened={isOpen}
        onClose={onClose}
        size="md"
        centered
        withCloseButton={false}
        closeOnClickOutside={true}
        padding={0}
        radius="xl"
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        styles={{
          content: {
            borderRadius: "1rem",
            overflow: "hidden",
          },
        }}
      >
        <div className="bg-white dark:bg-neutral-900">
          {/* Compact Header */}
          <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <MessageSquare size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Contact Host
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Send inquiry about your stay
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <X size={18} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Property/Room Info Banner */}
          <div className="px-5 py-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 border-b border-orange-100 dark:border-orange-800/30">
            <div className="flex items-center gap-2">
              <Home
                size={16}
                className="text-orange-600 dark:text-orange-400"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {propertyData.title}
                </p>
                {isSharedProperty && currentRoom && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                    Room: {currentRoom.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Compact Form */}
          <form onSubmit={formik.handleSubmit} className="p-5 space-y-3">
            {/* Date Pickers - Compact Grid */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <Calendar size={14} />
                  Check-in
                </label>
                <NomadDatePicker
                  placeholder="Add date"
                  disabled={
                    (isSharedProperty && !currentRoom) || // No room selected
                    (isSharedProperty &&
                      currentRoom &&
                      !selectedRoomHasAvailability) || // Room has no valid availability
                    (typeof propertyData?.next_available_date === "string" &&
                      propertyData.next_available_date !== formattedDate)
                  }
                  value={check_in_date || null}
                  onChange={(val) => {
                    console.log("Check-in onChange called with:", val);
                    formik.setFieldValue("check_in_date", val);
                    formik.setFieldValue("check_out_date", "");
                  }}
                  minDate={getMinimumDate()}
                  maxDate={getMaximumDate()}
                  excludeDate={disableDate}
                />
                {formik.touched.check_in_date &&
                  formik.errors.check_in_date && (
                    <div className="text-xs text-red-500 mt-1">
                      {formik.errors.check_in_date}
                    </div>
                  )}
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  <Calendar size={14} />
                  Check-out
                </label>
                <NomadDatePicker
                  placeholder="Add date"
                  disabled={
                    (isSharedProperty && !currentRoom) || // No room selected
                    (isSharedProperty &&
                      currentRoom &&
                      !selectedRoomHasAvailability) || // Room has no valid availability
                    !check_in_date || // Disable if no check-in selected
                    (typeof propertyData?.next_available_date === "string" &&
                      propertyData.next_available_date !== formattedDate)
                  }
                  value={check_out_date || null}
                  onChange={(val) => {
                    console.log("Check-out onChange called with:", val);
                    formik.setFieldValue("check_out_date", val);
                  }}
                  minDate={
                    check_in_date
                      ? moment(check_in_date).add(30, "days").toDate() // At least 30 days (1 month)
                      : getMinimumDate()
                  }
                  maxDate={getMaximumDate()} // Use room's availability_end or property's availability_end
                  excludeDate={disableDate}
                />
                {formik.errors.check_out_date &&
                  formik.touched.check_out_date && (
                    <div className="text-xs text-red-500 mt-1">
                      {formik.errors.check_out_date}
                    </div>
                  )}
              </div>
            </div>

            {/* Guests - Compact */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Users size={14} />
                Guests
              </label>
              <select
                name="guest_count"
                value={formik.values.guest_count || 1}
                onChange={formik.handleChange}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-900 dark:text-white border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
              >
                {Array.from({ length: 10 }, (_, n) => (
                  <option key={n} value={n + 1}>
                    {n + 1} {n === 0 ? "Guest" : "Guests"}
                  </option>
                ))}
              </select>
            </div>

            {/* Room Display (Read-only if shared property) */}
            {isSharedProperty && currentRoom && (
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Room
                </label>
                <div className="px-3 py-2 text-sm bg-gray-50 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg text-gray-900 dark:text-white flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  {currentRoom.name}
                </div>
              </div>
            )}

            {/* Message - Compact */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <MessageSquare size={14} />
                Your message
              </label>
              <textarea
                name="query"
                value={formik.values.query}
                onChange={formik.handleChange}
                placeholder="Hi! I'm interested in this property..."
                rows={3}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-900 dark:text-white border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none transition-all"
              />
              {formik.touched.query && formik.errors.query && (
                <div className="text-xs text-red-500 mt-1">
                  {formik.errors.query}
                </div>
              )}
            </div>

            {/* Compact Submit Button */}
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
            >
              {formik.isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </Modal>

      <CommonModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          onClose();
        }}
        description={Description}
      />
    </React.Fragment>
  );
};

export default ContactOwnerModal;
